import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'

import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from '@/components/ui/hover-card'
import {
	Mail,
	MessageSquare,
	Scale,
	List,
	Star,
	ChevronDown,
	ChevronUp,
	Monitor,
	Smartphone,
	Globe,
	CheckCircle,
	Lock,
} from 'lucide-react'
import { format, formatDistanceToNow } from 'date-fns'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { cn } from '@/lib/utils'
import { useState, useEffect } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { ResponseDetailView } from './ResponseDetailView'
import { useTranslation } from '@/hooks/useTranslation'
import { useSearchParams, useNavigate } from 'react-router-dom'

interface UserMetadata {
	email?: string
	name?: string
	browser?: string
	device?: string
	location?: string
	pageUrl?: string
	language?: string
	referrer?: string
	timezone?: string
	pageTitle?: string
	timestamp?: string
	userAgent?: string
	screenSize?: {
		width: number
		height: number
	}
}

interface ResponseData {
	id: string
	created_at: string
	program_id: string | null
	score: number | null
	response_data: any
	user_metadata: UserMetadata | null
	widgets: {
		name: string
		type: string
		question: string
	} | null
	is_verified?: boolean
	email?: string
	name?: string
	social_media_link?: string
	submission_type?: string
}

interface ResponsesTableProps {
	responses?: ResponseData[]
	onResponseDeleted?: () => void
	onResponseSelect?: (responseId: string) => void
	className?: string
	isPro?: boolean
	hiddenCount?: number
	itemsPerPage?: number
	sort?: string
	onSortChange?: (sort: string) => void
}

const getQuestionIcon = (type: string) => {
	switch (type) {
		case 'email':
			return <Mail className="h-4 w-4 text-blue-500" />
		case 'text':
			return <MessageSquare className="h-4 w-4 text-green-500" />
		case 'scale':
			return <Scale className="h-4 w-4 text-yellow-500" />
		case 'multiple_choice':
			return <List className="h-4 w-4 text-purple-500" />
		default:
			return <Star className="h-4 w-4 text-gray-500" />
	}
}

const getNPSCategory = (score: number, t: (key: string) => string) => {
	if (score >= 9) return t('responsesTable.promoter')
	if (score >= 7) return t('responsesTable.passive')
	return t('responsesTable.detractor')
}

const getNPSBadgeColor = (score: number) => {
	if (score >= 9) return 'bg-green-50 text-green-700'
	if (score >= 7) return 'bg-yellow-50 text-yellow-700'
	return 'bg-red-50 text-red-700'
}

const getCSATLabel = (score: number, t: (key: string) => string) => {
	const labels = [
		t('responsesTable.veryUnsatisfied'),
		t('responsesTable.unsatisfied'),
		t('responsesTable.neutral'),
		t('responsesTable.satisfied'),
		t('responsesTable.verySatisfied'),
	]
	return labels[score - 1] || t('responsesTable.unknown')
}

interface SortableHeaderProps {
	children: React.ReactNode
	sortKey: string
	currentSort?: string
	onSortChange?: (sort: string) => void
	className?: string
}

function SortableHeader({
	children,
	sortKey,
	currentSort,
	onSortChange,
	className,
}: SortableHeaderProps) {
	const isActive = currentSort?.startsWith(sortKey)
	const isDesc = currentSort === `${sortKey}_desc`

	const handleClick = () => {
		if (!onSortChange) return

		if (!isActive) {
			onSortChange(`${sortKey}_desc`)
		} else if (isDesc) {
			onSortChange(`${sortKey}_asc`)
		} else {
			onSortChange(`${sortKey}_desc`)
		}
	}

	return (
		<TableHead
			className={cn(
				'cursor-pointer hover:bg-muted/50 transition-colors select-none',
				className
			)}
			onClick={handleClick}>
			<div className="flex items-center gap-1">
				{children}
				<div className="flex flex-col">
					<ChevronUp
						className={cn(
							'h-3 w-3 transition-colors',
							isActive && !isDesc ? 'text-primary' : 'text-muted-foreground/50'
						)}
					/>
					<ChevronDown
						className={cn(
							'h-3 w-3 -mt-1 transition-colors',
							isActive && isDesc ? 'text-primary' : 'text-muted-foreground/50'
						)}
					/>
				</div>
			</div>
		</TableHead>
	)
}

const ResponseSummary = ({ response }: { response: ResponseData }) => {
	const { t } = useTranslation()
	const submissionType = response.submission_type || response.widgets?.type
	const { score, response_data } = response

	if (!response.widgets) {
		return (
			<span className="text-sm text-muted-foreground italic">
				{t('responsesTable.widgetDataMissing')}
			</span>
		)
	}

	if (submissionType === 'nps' && score !== null) {
		return (
			<Badge className={cn('font-medium', getNPSBadgeColor(score))}>
				{getNPSCategory(score, t)} ({score}/10)
			</Badge>
		)
	}
	if (submissionType === 'csat' && score !== null) {
		return (
			<Badge className="bg-blue-50 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 font-medium">
				{getCSATLabel(score, t)} ({score}/5)
			</Badge>
		)
	}
	if (
		(submissionType === 'review' || submissionType === 'testimonial') &&
		score !== null
	) {
		return (
			<div className="flex items-center gap-1">
				{[...Array(5)].map((_, i) => (
					<Star
						key={i}
						className={cn(
							'h-4 w-4',
							i < score
								? 'fill-yellow-400 text-yellow-400'
								: 'text-gray-300 dark:text-slate-600'
						)}
					/>
				))}
				<span className="text-xs text-muted-foreground ml-1">({score}/5)</span>
			</div>
		)
	}

	if (response_data?.customAnswers) {
		const answers = Object.values(response_data.customAnswers)
		if (answers.length > 0) {
			const firstAnswer = answers[0] as { answer: string; questionText: string }
			return (
				<div className="space-y-1">
					<p className="text-xs text-muted-foreground">
						{firstAnswer.questionText}
					</p>
					<p className="text-sm truncate max-w-[600px]">{firstAnswer.answer}</p>
				</div>
			)
		}
	}

	if (
		(submissionType === 'testimonial' || submissionType === 'review') &&
		response_data?.testimonialText
	) {
		return (
			<div className="flex items-center gap-2">
				<p className="text-sm truncate max-w-[400px] italic">
					"{response_data.testimonialText}"
				</p>
				{response.is_verified && (
					<Badge
						variant="outline"
						className="bg-green-50 text-green-700 gap-1 flex items-center">
						<CheckCircle className="h-3 w-3" />
						{t('responsesTable.verified')}
					</Badge>
				)}
			</div>
		)
	}

	// Handle simple feedback field (from seeded data)
	if (response_data?.feedback) {
		return (
			<div className="space-y-1">
				<p className="text-sm text-muted-foreground italic line-clamp-2">
					"{response_data.feedback}"
				</p>
				{response_data.nps_category && (
					<Badge
						className={cn(
							'font-medium text-xs',
							response_data.nps_category === 'promoter'
								? 'bg-green-50 text-green-700'
								: response_data.nps_category === 'passive'
								? 'bg-yellow-50 text-yellow-700'
								: 'bg-red-50 text-red-700'
						)}>
						{response_data.nps_category}
					</Badge>
				)}
			</div>
		)
	}

	return (
		<span className="text-sm text-muted-foreground italic">
			{t('responsesTable.noResponseData')}
		</span>
	)
}

const CustomAnswersDetail = ({ responseData }: { responseData: any }) => {
	const { t } = useTranslation()
	if (
		responseData?.customAnswers &&
		typeof responseData.customAnswers === 'object'
	) {
		const answers = Object.entries(
			responseData.customAnswers as Record<string, any>
		)
		if (answers.length > 0) {
			return (
				<div className="space-y-3">
					{answers.map(([id, data]) => (
						<div
							key={id}
							className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-md border dark:border-gray-700/50">
							<div className="flex items-start gap-2">
								<div className="mt-1">
									{getQuestionIcon(data.type || 'text')}
								</div>
								<div className="space-y-1 flex-1">
									<p className="text-xs font-medium text-gray-500 dark:text-gray-400">
										{data.questionText}
									</p>
									<p className="text-sm text-gray-800 dark:text-gray-200">
										{data.answer}
									</p>
								</div>
							</div>
						</div>
					))}
				</div>
			)
		}
	}

	if (
		responseData &&
		typeof responseData === 'object' &&
		!responseData.customAnswers
	) {
		const entries = Object.entries(responseData)
		if (entries.length > 0) {
			return (
				<div className="space-y-3">
					<p className="text-xs text-orange-500 italic">
						{t('responsesTable.noteDisplayingOlderFormat')}
					</p>
					{entries.map(([key, value]) => (
						<div
							key={key}
							className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-md border dark:border-gray-700/50">
							<p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
								{t('responsesTable.questionId')}:{' '}
								<code className="text-xs">{key}</code>
							</p>
							<p className="text-sm text-gray-800 dark:text-gray-200">
								{String(value)}
							</p>
						</div>
					))}
				</div>
			)
		}
	}

	return (
		<p className="text-sm text-muted-foreground italic">
			{t('responsesTable.noCustomAnswersProvided')}
		</p>
	)
}

const FullResponseDetail = ({ response }: { response: ResponseData }) => {
	const { t } = useTranslation()
	const {
		response_data,
		widgets,
		is_verified,
		email,
		name,
		social_media_link,
	} = response
	const { type } = widgets

	const renderReviewInfo = () => {
		return (
			<div className="mt-3 space-y-2">
				{is_verified && (
					<div className="flex items-center gap-2">
						<Badge
							variant="outline"
							className="bg-green-50 text-green-700 gap-1 flex items-center">
							<CheckCircle className="h-3 w-3" />
							{t('responsesTable.verifiedReview')}
						</Badge>
						<span className="text-xs text-muted-foreground">
							{t('responsesTable.reviewVerifiedViaEmail')}
						</span>
					</div>
				)}

				{email && (
					<div className="flex items-center gap-2">
						<Mail className="h-4 w-4 text-muted-foreground" />
						<span className="text-sm">{email}</span>
					</div>
				)}

				{name && (
					<div className="flex items-center gap-2">
						<span className="text-sm font-medium">{name}</span>
					</div>
				)}

				{social_media_link && (
					<div className="flex items-center gap-2">
						<Globe className="h-4 w-4 text-muted-foreground" />
						<a
							href={social_media_link}
							target="_blank"
							rel="noopener noreferrer"
							className="text-sm text-blue-600 hover:underline">
							{social_media_link}
						</a>
					</div>
				)}
			</div>
		)
	}

	return (
		<div className="p-4 space-y-4">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<Badge
						variant="outline"
						className={cn(
							type === 'nps'
								? 'bg-purple-50 text-purple-700'
								: type === 'csat'
								? 'bg-blue-50 text-blue-700'
								: 'bg-gray-50 text-gray-700'
						)}>
						{widgets.type?.toUpperCase() || t('responsesTable.notAvailable')}
					</Badge>
					<h3 className="text-sm font-medium">{widgets.question}</h3>
				</div>
			</div>

			{response.score !== null && (
				<div className="flex items-center gap-3">
					<div className="text-xl font-semibold">{response.score}</div>
					{type === 'nps' && (
						<Badge
							className={cn('font-medium', getNPSBadgeColor(response.score))}>
							{getNPSCategory(response.score, t)}
						</Badge>
					)}
					{type === 'csat' && response.score && (
						<div className="text-sm">{getCSATLabel(response.score, t)}</div>
					)}
				</div>
			)}

			{(type === 'testimonial' || type === 'review') &&
				response_data?.testimonialText && (
					<>
						<blockquote className="pl-3 border-l-2 border-gray-200 italic">
							"{response_data.testimonialText}"
						</blockquote>
						{renderReviewInfo()}
					</>
				)}

			{response_data?.customAnswers && (
				<CustomAnswersDetail responseData={response_data} />
			)}
		</div>
	)
}

const UserMetadataDetail = ({ metadata }: { metadata: UserMetadata }) => {
	const { t } = useTranslation()
	const details = [
		{ label: t('responsesTable.pageTitle'), value: metadata.pageTitle },
		{ label: t('responsesTable.pageUrl'), value: metadata.pageUrl, link: true },
		{ label: t('responsesTable.device'), value: metadata.device },
		{ label: t('responsesTable.browser'), value: metadata.browser },
		{ label: t('responsesTable.location'), value: metadata.location },
		{ label: t('responsesTable.language'), value: metadata.language },
		{ label: t('responsesTable.timezone'), value: metadata.timezone },
		{
			label: t('responsesTable.screen'),
			value: metadata.screenSize
				? `${metadata.screenSize.width}×${metadata.screenSize.height}`
				: null,
		},
		{
			label: t('responsesTable.referrer'),
			value: metadata.referrer,
			link: true,
		},
		{
			label: t('responsesTable.timestamp'),
			value: metadata.timestamp
				? format(new Date(metadata.timestamp), 'Pp')
				: null,
		},
	].filter((d) => d.value)

	if (details.length === 0 && !metadata.userAgent) {
		return (
			<p className="text-sm text-muted-foreground italic">
				{t('responsesTable.noSubmissionDetails')}
			</p>
		)
	}

	return (
		<div className="space-y-3">
			<div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
				{details.map((detail) => (
					<div key={detail.label} className="flex flex-col overflow-hidden">
						<span className="font-medium text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide mb-0.5">
							{detail.label}
						</span>
						{detail.link && detail.value ? (
							<a
								href={detail.value}
								target="_blank"
								rel="noopener noreferrer"
								className="text-blue-600 hover:underline truncate">
								{detail.value}
							</a>
						) : (
							<span className="text-gray-800 dark:text-gray-200 truncate">
								{detail.value}
							</span>
						)}
					</div>
				))}
			</div>
			{metadata.userAgent && (
				<div className="mt-3 pt-3 border-t dark:border-gray-700/50">
					<span className="font-medium text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wide mb-1 block">
						{t('responsesTable.userAgent')}
					</span>
					<p className="text-xs text-gray-500 dark:text-gray-400 break-all bg-gray-50 dark:bg-gray-800/50 p-2 rounded-md border dark:border-gray-700/50">
						{metadata.userAgent}
					</p>
				</div>
			)}
		</div>
	)
}

const DeviceInfo = ({ metadata }: { metadata?: UserMetadata | null }) => {
	const { t } = useTranslation()
	if (!metadata) {
		return (
			<span className="text-xs text-muted-foreground italic">
				{t('responsesTable.notAvailable')}
			</span>
		)
	}
	if (
		!metadata.device &&
		!metadata.browser &&
		!metadata.location &&
		!metadata.pageTitle &&
		!metadata.timezone &&
		!metadata.screenSize
	) {
		return (
			<span className="text-xs text-muted-foreground italic">
				{t('responsesTable.notAvailable')}
			</span>
		)
	}

	return (
		<div className="flex items-center gap-1 mt-1">
			{metadata.device && (
				<HoverCard>
					<HoverCardTrigger>
						{metadata.device.toLowerCase().includes('mobile') ? (
							<Smartphone className="h-3.5 w-3.5 text-gray-400" />
						) : (
							<Monitor className="h-3.5 w-3.5 text-gray-400" />
						)}
					</HoverCardTrigger>
					<HoverCardContent className="text-xs p-2 w-auto">
						{metadata.device}
					</HoverCardContent>
				</HoverCard>
			)}
			{metadata.browser && (
				<HoverCard>
					<HoverCardTrigger>
						<Globe className="h-3.5 w-3.5 text-gray-400" />
					</HoverCardTrigger>
					<HoverCardContent className="text-xs p-2 w-auto">
						{metadata.browser}
					</HoverCardContent>
				</HoverCard>
			)}
		</div>
	)
}

const ResponsesTable = ({
	responses = [],
	onResponseDeleted,
	onResponseSelect,
	className,
	isPro = true,
	hiddenCount = 0,
	itemsPerPage = 10,
	sort,
	onSortChange,
}: ResponsesTableProps) => {
	const { t } = useTranslation()
	const { toast } = useToast()
	const queryClient = useQueryClient()
	const navigate = useNavigate()

	const deleteResponseMutation = useMutation({
		mutationFn: async (responseId: string) => {
			const {
				data: { user },
				error: userError,
			} = await supabase.auth.getUser()
			if (userError) {
				console.error('Error getting current user:', userError)
				throw new Error(t('responsesTable.authenticationError'))
			}

			const { data: teamData, error: teamError } = await supabase
				.from('team_members')
				.select('team_id, teams(name)')
				.eq('user_id', user?.id)

			if (teamError) {
				console.error('Error getting user teams:', teamError)
				throw new Error(t('responsesTable.couldNotVerifyTeamMembership'))
			}

			if (!teamData || teamData.length === 0) {
				console.error('User is not a member of any team:', user?.id)
				throw new Error(t('responsesTable.mustBeMemberOfTeam'))
			}

			const { data: verifyData, error: verifyError } = await supabase
				.from('responses')
				.select('id, team_id, widgets(name)')
				.eq('id', responseId)
				.single()

			if (verifyError) {
				console.error('Error verifying response access:', verifyError)
				throw new Error(t('responsesTable.couldNotVerifyAccess'))
			}

			if (!verifyData) {
				console.error('Response not found:', responseId)
				throw new Error(t('responsesTable.responseNotFound'))
			}

			const hasAccess = teamData.some(
				(team) => team.team_id === verifyData.team_id
			)

			if (!hasAccess) {
				console.error('User does not have access to this response')
				throw new Error(t('responsesTable.noPermissionToDelete'))
			}

			const { data, error } = await supabase
				.from('responses')
				.delete()
				.eq('id', responseId)
				.select()

			if (error) {
				console.error('Error deleting response:', error)
				throw error
			}

			if (!data || data.length === 0) {
				console.error('No data returned after deletion')
				throw new Error(t('responsesTable.failedToDeleteResponse'))
			}

			return data[0]
		},
		onSuccess: () => {
			toast({
				title: t('responsesTable.responseDeletedSuccess'),
				description: t('responsesTable.responseDeletedDescription'),
			})
			queryClient.invalidateQueries({ queryKey: ['responses'] })
			onResponseDeleted?.()
		},
		onError: (error: any) => {
			console.error('Mutation error:', error)
			toast({
				title: t('responsesTable.errorDeletingResponse'),
				description:
					error.message || t('responsesTable.unexpectedErrorOccurred'),
				variant: 'destructive',
			})
		},
	})

	const handleRowClick = (e: React.MouseEvent, responseId: string) => {
		if ((e.target as HTMLElement).closest('.upgrade-button')) {
			return
		}
		onResponseSelect?.(responseId)
	}

	const displayedResponses = responses || []

	// Show blur effect for free users when they have hidden content
	// regardless of whether current page is full
	const numberOfBlurredRows =
		!isPro && hiddenCount > 0
			? Math.min(3, hiddenCount) // Always show up to 3 blurred rows when there are hidden responses
			: 0

	if (displayedResponses.length === 0 && numberOfBlurredRows === 0) {
		return (
			<Card
				className={cn(
					'bg-white dark:bg-slate-900 border-border/50',
					className
				)}>
				<CardContent>
					<div className="text-center py-12 text-muted-foreground">
						<CheckCircle className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
						<h3 className="text-lg font-semibold">
							{t('responsesTable.noResponsesHere')}
						</h3>
						<p className="mt-1 text-sm">
							{t('responsesTable.noResponsesFoundMatchingFilters')}
						</p>
					</div>
				</CardContent>
			</Card>
		)
	}

	return (
		<>
			<Card
				className={cn(
					'relative overflow-hidden border-border/50 shadow-sm bg-white dark:bg-slate-900',
					className
				)}>
				<div className="overflow-x-auto">
					<Table>
						<TableHeader>
							<TableRow className="hover:bg-transparent border-t-0 bg-slate-50/50 dark:bg-slate-800/50 sticky top-0 z-10">
								<TableHead className="min-w-[100px] px-4 py-3">
									{t('responsesTable.widget')}
								</TableHead>
								<TableHead className="min-w-[300px] px-4 py-3">
									{t('responsesTable.summary')}
								</TableHead>
								<SortableHeader
									sortKey="created_at"
									currentSort={sort}
									onSortChange={onSortChange}
									className="w-[160px] px-4 py-3">
									{t('responsesTable.date')}
								</SortableHeader>
							</TableRow>
						</TableHeader>
						<TableBody>
							{!isPro &&
								numberOfBlurredRows > 0 &&
								Array.from({ length: numberOfBlurredRows }).map((_, index) => {
									const placeholderResponses = [
										{
											widget: t('responsesTable.placeholderWidget1'),
											type: 'CSAT',
											summary: t('responsesTable.placeholderSummary1'),
											score: 5,
											feedback: t('responsesTable.placeholderFeedback1'),
											time: t('responsesTable.placeholderTime1'),
										},
										{
											widget: t('responsesTable.placeholderWidget2'),
											type: 'NPS',
											summary: t('responsesTable.placeholderSummary2'),
											score: 9,
											feedback: t('responsesTable.placeholderFeedback2'),
											time: t('responsesTable.placeholderTime2'),
										},
										{
											widget: t('responsesTable.placeholderWidget3'),
											type: 'NPS',
											summary: t('responsesTable.placeholderSummary3'),
											score: 10,
											feedback: t('responsesTable.placeholderFeedback3'),
											time: t('responsesTable.placeholderTime3'),
										},
										{
											widget: t('responsesTable.placeholderWidget4'),
											type: 'CSAT',
											summary: t('responsesTable.placeholderSummary4'),
											score: 4,
											feedback: t('responsesTable.placeholderFeedback4'),
											time: t('responsesTable.placeholderTime4'),
										},
										{
											widget: t('responsesTable.placeholderWidget5'),
											type: 'CUSTOM',
											summary: t('responsesTable.placeholderSummary5'),
											score: null,
											feedback: t('responsesTable.placeholderFeedback5'),
											time: t('responsesTable.placeholderTime5'),
										},
									]

									const placeholder =
										placeholderResponses[index % placeholderResponses.length]

									return (
										<TableRow
											key={`blurred-response-${index}`}
											className="relative pointer-events-none select-none filter blur-[3px] opacity-30 border-b dark:border-slate-700/50">
											<TableCell className="align-top py-3 px-4">
												<div className="flex items-center gap-2.5">
													<div className="flex flex-col">
														<span className="font-medium text-sm line-clamp-1">
															{placeholder.widget}
														</span>
														<Badge
															variant="outline"
															className="mt-0.5 font-normal text-xs w-fit capitalize border-slate-300 dark:border-slate-600 text-muted-foreground">
															{placeholder.type}
														</Badge>
													</div>
												</div>
											</TableCell>
											<TableCell className="align-top py-3 px-4">
												<div className="space-y-1">
													<Badge
														className={cn(
															'font-medium',
															placeholder.score >= 9
																? 'bg-green-50 text-green-700'
																: placeholder.score >= 7
																? 'bg-yellow-50 text-yellow-700'
																: placeholder.score >= 4
																? 'bg-blue-50 text-blue-700'
																: 'bg-red-50 text-red-700'
														)}>
														{placeholder.summary}
													</Badge>
													<p className="text-sm text-muted-foreground italic line-clamp-2">
														"{placeholder.feedback}"
													</p>
												</div>
											</TableCell>
											<TableCell className="align-top py-3 px-4 text-xs text-muted-foreground">
												<div>{t('responsesTable.placeholderDate')}</div>
												<div className="text-slate-500 dark:text-slate-400">
													{t('responsesTable.placeholderTimeValue')}
												</div>
												<div className="mt-1 text-slate-500 dark:text-slate-400">
													{placeholder.time}
												</div>
											</TableCell>
										</TableRow>
									)
								})}

							{/* Actual responses below blurred content */}
							{displayedResponses.map((response) => (
								<TableRow
									key={response.id}
									className={cn(
										'group hover:bg-slate-50/80 dark:hover:bg-slate-800/80 transition-colors border-b dark:border-slate-200 dark:border-slate-700/70 cursor-pointer'
									)}
									onClick={(e) => handleRowClick(e, response.id)}>
									<TableCell className="align-top py-3 px-4">
										<div className="flex items-center gap-2.5">
											<div className="flex flex-col">
												<span className="font-medium text-sm line-clamp-1 group-hover:text-primary transition-colors">
													{response.widgets?.name ||
														`${t('responsesTable.unknown')} ${t(
															'responsesTable.widget'
														)}`}
												</span>
												<Badge
													variant="outline"
													className="mt-0.5 font-normal text-xs w-fit capitalize border-slate-300 dark:border-slate-600 text-muted-foreground">
													{response.submission_type?.toUpperCase() ||
														response.widgets?.type?.toUpperCase() ||
														t('responsesTable.notAvailable')}
												</Badge>
											</div>
										</div>
									</TableCell>
									<TableCell className="align-top py-3 px-4">
										<ResponseSummary response={response} />
									</TableCell>
									<TableCell className="align-top py-3 px-4 text-xs text-muted-foreground">
										<div>
											{format(new Date(response.created_at), 'MMM d, yyyy')}
										</div>
										<div className="text-slate-500 dark:text-slate-400">
											{format(new Date(response.created_at), 'p')}
										</div>
										<div className="mt-1 text-slate-500 dark:text-slate-400">
											{formatDistanceToNow(new Date(response.created_at), {
												addSuffix: true,
											})}
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
					{/* Upgrade Button Overlay - positioned in center of blurred rows at TOP */}
					{!isPro && numberOfBlurredRows > 0 && (
						<div
							className="absolute left-0 right-0 flex items-center justify-center pointer-events-none z-30"
							style={{
								top: `${66 + (numberOfBlurredRows * 65) / 2 - 25}px`, // Header + half of blurred rows - half button height
								height: '50px',
							}}>
							<div className="pointer-events-auto">
								<Button
									variant="default"
									size="lg"
									className="upgrade-button shadow-xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold animate-pulse border-2 border-primary-foreground/20"
									onClick={(e) => {
										e.stopPropagation()
										navigate('/settings?tab=billing')
									}}>
									<Lock className="h-5 w-5 mr-2.5" />
									{t('upgrade.upgradeToPro')} - {t('responsesTable.view')}{' '}
									{hiddenCount > 1
										? `${hiddenCount} ${t('responsesTable.moreResponses')}`
										: `1 ${t('responsesTable.moreResponse')}`}
								</Button>
							</div>
						</div>
					)}
				</div>
			</Card>
		</>
	)
}

export const ResponsesTableSkeleton = () => {
	return (
		<Card className="overflow-hidden bg-white dark:bg-slate-900 border-border/50">
			<div className="overflow-x-auto">
				<Table>
					<TableHeader>
						<TableRow className="hover:bg-transparent border-t-0 bg-gray-50/50 dark:bg-slate-800/50">
							<TableHead className="w-12 px-2"></TableHead>
							<TableHead className="w-[220px] px-4 py-3">
								<Skeleton className="h-4 w-20 rounded" />
							</TableHead>
							<TableHead className="px-4 py-3">
								<Skeleton className="h-4 w-24 rounded" />
							</TableHead>
							<TableHead className="w-[150px] px-4 py-3">
								<Skeleton className="h-4 w-16 rounded" />
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{Array.from({ length: 8 }).map((_, index) => (
							<TableRow
								key={index}
								className="border-b dark:border-slate-700/50">
								<TableCell className="w-12 align-top py-4 pl-3 pr-1">
									<Skeleton className="h-8 w-8 rounded-md" />
								</TableCell>
								<TableCell className="align-top py-4 px-4">
									<div className="flex items-center gap-2">
										<Skeleton className="h-8 w-8 rounded-full" />
										<div className="flex flex-col gap-1.5">
											<Skeleton className="h-4 w-24 rounded" />
											<Skeleton className="h-4 w-16 rounded" />
										</div>
									</div>
								</TableCell>
								<TableCell className="align-top py-4 px-4">
									<Skeleton className="h-5 w-32 rounded-full" />
								</TableCell>
								<TableCell className="align-top py-4 px-4">
									<div className="flex flex-col gap-1.5">
										<Skeleton className="h-4 w-20 rounded" />
										<Skeleton className="h-4 w-16 rounded" />
										<Skeleton className="h-4 w-20 rounded" />
									</div>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		</Card>
	)
}

export default ResponsesTable
